import { DownOutlined } from '@ant-design/icons'
import { App, Button, Dropdown, MenuProps } from 'antd'
import React, { useCallback, useMemo } from 'react'

import { FunctionKey } from '@/constants/function-key'
import useFormModal from '@/hooks/use-form-modal'
import {
  removeEmployee,
  updateEmployee,
} from '@/redux/employee-actions'
import { selectMineFunctionKeys } from '@/redux/function-permission-selector'
import { useAppDispatch, useAppSelector } from '@/redux/utils'

import {
  BatchDeleteFields,
  BatchEditGroupFields,
  BatchEditRolesFields,
} from './batch-operations-fields'

export interface BatchOperationsOptionsProps {
  employeeIds: number[];
}

export default function BatchOperationsOptions(
  props: BatchOperationsOptionsProps,
) {
  const { employeeIds } = props
  const { message } = App.useApp()
  const dispatch = useAppDispatch()

  const mineFunctionKeys = useAppSelector(selectMineFunctionKeys)

  const onBatchEditGroup = useCallback(
    async ({ groupId }: { groupId: number }) => {
      await Promise.all(employeeIds.map(
        (id) => dispatch(updateEmployee({ id, groupId })).unwrap(),
      ))
      void message.success('Batch edit group successfully!')
    },
    [dispatch, employeeIds, message],
  )

  const onBatchEditRoles = useCallback(
    async ({ roleIds }: { roleIds: number[] }) => {
      await Promise.all(employeeIds.map(
        (id) => dispatch(updateEmployee({ id, roleIds })).unwrap(),
      ))
      void message.success('Batch edit roles successfully!')
    },
    [dispatch, employeeIds, message],
  )

  const onBatchDelete = useCallback(async () => {
    await Promise.all(employeeIds.map(
      (id) => dispatch(removeEmployee(id)).unwrap(),
    ))
    void message.success('Batch delete successfully!')
  }, [dispatch, employeeIds, message])

  const [batchEditGroupFormModal, openEditGroupModal] = useFormModal({
    onSubmit: onBatchEditGroup,
    getFields: () => <BatchEditGroupFields selectedEmployeeIds={employeeIds} />,
    title: 'Batch edit group',
    formProps: { layout: 'horizontal' },
    submitText: 'Assign',
    modalProps: { centered: true },
  })

  const [batchEditRolesFormModal, openEditRolesModal] = useFormModal({
    onSubmit: onBatchEditRoles,
    getFields: () => <BatchEditRolesFields selectedEmployeeIds={employeeIds} />,
    title: 'Batch edit roles',
    formProps: { layout: 'horizontal' },
    submitText: 'Assign',
    modalProps: { centered: true },
  })

  const [batchDeleteFormModal, openDeleteModal] = useFormModal({
    onSubmit: onBatchDelete,
    getFields: () => <BatchDeleteFields selectedEmployeeIds={employeeIds} />,
    title: 'Batch delete',
    submitText: 'Assign',
    modalProps: { centered: true },
  })

  const checkSelected = useCallback(() => {
    if (employeeIds.length === 0) {
      void message.warning('Please select at least one employee!')
      return false
    }
    return true
  }, [message, employeeIds])

  const items: MenuProps['items'] = useMemo(() => {
    return [
      mineFunctionKeys.includes(FunctionKey.EmployeesManageEditGroup) && {
        key: '1',
        label: 'Batch edit group',
        onClick() {
          if (checkSelected()) openEditGroupModal()
        },
      },
      mineFunctionKeys.includes(FunctionKey.EmployeesManageSetupRole) && {
        key: '2',
        label: 'Batch edit roles',
        onClick() {
          if (checkSelected()) openEditRolesModal()
        },
      },
      mineFunctionKeys.includes(FunctionKey.EmployeesManageDeleteEmployee) && {
        key: '3',
        label: 'Batch delete',
        onClick() {
          if (checkSelected()) openDeleteModal()
        },
      },
    ].filter(Boolean) as MenuProps['items']
  }, [checkSelected, openDeleteModal, openEditGroupModal, openEditRolesModal, mineFunctionKeys])

  return (
    <>
      <Dropdown menu={{ items }} placement="bottomLeft">
        <Button type="primary" ghost>
          Batch Operations <DownOutlined />
        </Button>
      </Dropdown>
      {batchEditGroupFormModal}
      {batchEditRolesFormModal}
      {batchDeleteFormModal}
    </>
  )
}
